// Adjustable cabinet legs (Blum / Hettich / generic).
//
// A `Leg` is a nested object on `Cabinet.legs[]`. Each evaluates to
// one vertical box mesh chunk (we don't have cylinders in the boxChunk
// primitive — a 1.5″ × 1.5″ square is a fine approximation at editor
// scale) plus one hardware reference for the BOM.
//
// The carcass's vertical lift (the `TOE_KICK_H` class param) should
// match the leg height so the cabinet sits flush on top of its legs.
// The constructor UI in `LegsSection.tsx` keeps the two in sync.

import type {
  Cabinet,
  CabinetMeshChunk,
  Leg,
} from "@/lib/types";
import type { DrillEntry } from "./index";
import { boxChunk, COLORS } from "./geometry";

/** Default square cross-section width for a rendered leg. */
const DEFAULT_LEG_DIAMETER = 1.5;
/** Default inset from the cabinet sides — gives clearance for the
 *  kick board and slight finger room when installing. */
export const DEFAULT_LEG_INSET_X = 2;
/** Default inset from the cabinet front/back. Front legs sit so their
 *  front face meets the back face of the toe-kick board (clip-on
 *  install): for the seed's TOE_KICK_D=3, T=0.75, and a 1.5″ leg, that
 *  puts the leg centre 3 + T + LEG_DIAMETER/2 = 4.5″ back from the
 *  cabinet face. */
export const DEFAULT_LEG_INSET_Z = 4.5;
/** Default leg height — matches the seed's TOE_KICK_H. */
export const DEFAULT_LEG_HEIGHT = 4;

export interface LegEvalResult {
  meshes: CabinetMeshChunk[];
}

export function evaluateLeg(cabinet: Cabinet, leg: Leg): LegEvalResult {
  const d = leg.diameter ?? DEFAULT_LEG_DIAMETER;
  const half = d / 2;
  void cabinet;
  return {
    meshes: [boxChunk(
      `leg_${leg._id}`,
      [leg.x - half, 0,           leg.z - half],
      [leg.x + half, leg.height,  leg.z + half],
      COLORS.carcass,
    )],
  };
}

export function evaluateAllLegs(cabinet: Cabinet): LegEvalResult {
  const out: LegEvalResult = { meshes: [] };
  for (const l of cabinet.legs ?? []) {
    out.meshes.push(...evaluateLeg(cabinet, l).meshes);
  }
  return out;
}

/** Build the 4 (or 5) corner-default legs for a cabinet. Wider units
 *  pick up a 5th leg in the middle of the back so a long carcass
 *  doesn't sag. Inset matches shop practice; the caller can edit
 *  positions later if needed. */
export function defaultLegs(cabinet: Cabinet, height = DEFAULT_LEG_HEIGHT): Leg[] {
  const insetX = DEFAULT_LEG_INSET_X;
  const insetZ = DEFAULT_LEG_INSET_Z;
  const W = cabinet.width;
  const D = cabinet.depth;
  const corners: Array<[number, number]> = [
    [insetX,     insetZ    ],
    [W - insetX, insetZ    ],
    [insetX,     D - insetZ],
    [W - insetX, D - insetZ],
  ];
  // Wide cabinets (>36″) get a centre-back support.
  if (W > 36) corners.push([W / 2, D - insetZ]);
  return corners.map(([x, z], i) => ({
    _id: i + 1,
    x, z,
    height,
    diameter: DEFAULT_LEG_DIAMETER,
  }));
}

/** Total leg count for BOM allocation. */
export function legHardwareCount(cabinet: Cabinet): number {
  return cabinet.legs?.length ?? 0;
}

// ── Leg-mount drillings on the carcass bottom panel ──────────────
//
// Each adjustable leg attaches via one mounting screw driven up through
// the underside of the bottom panel into the leg base. Threaded inserts
// add a second hole but those are press-fit by the leg supplier; we emit
// the single carcass-side pilot.
//
// Bottom panel in BaseCabinet's parts: cut.w = `W - 2*T`, cut.h = `D`.
// Cabinet x maps to cut-X = (x - T); cabinet z maps to cut-Y = z.

/** Carcass panel thickness — matches the seed's `T`. */
const CARCASS_T = 0.75;
/** M5 mounting screw — Blum / Hettich adjustable feet use M5. */
const LEG_SCREW_DIAM = 5 / 25.4;
/** Pilot depth — shy of full panel thickness to avoid blowout topside. */
const LEG_SCREW_DEPTH = 14 / 25.4;

/** Drilling entries for the bottom-panel half of each adjustable leg.
 *  Wall- and ceiling-anchored cabinets have no legs and so emit nothing.
 *  Keyed by the bottom-panel part id (`bottom`). */
export function legDrillings(cabinet: Cabinet): Record<string, DrillEntry[]> {
  const legs = cabinet.legs ?? [];
  if (legs.length === 0) return {};

  const entries: DrillEntry[] = legs.map((l) => ({
    x: l.x - CARCASS_T,
    y: l.z,
    diameter: LEG_SCREW_DIAM,
    depth: LEG_SCREW_DEPTH,
    face: "back",
    kind: "screw",
  }));

  return { bottom: entries };
}
